import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Mic, ArrowRight, Zap, CheckCircle2, 
  CreditCard, ListChecks, Layers, ShieldCheck, ArrowLeft,
  Volume2
} from 'lucide-react';
import Footer from '@/components/Footer';

const VozInfo = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Volver
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
                  Ver Planes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-stone-200/70 border border-stone-300 text-stone-800 px-3.5 py-1.5 rounded-full mb-6 text-xs sm:text-sm font-semibold shadow-xs">
              <Mic className="w-4 h-4 text-indigo-600" />
              <span>Dictado Inteligente de Gastos</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
              Registra tus compras solo{' '}
              <span className="text-indigo-600">hablando con naturalidad</span>
            </h1>

            <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
              Olvida llenar formularios manuales mientras sales de la tienda. Dicta tus compras, deja que la IA desglose cada artículo y asigne la cuenta bancaria en un instante.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Probar Gratis Ahora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-300 bg-white hover:bg-stone-100 text-stone-800 font-bold text-sm sm:text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </div>

          {/* Interactive Visual Demonstration */}
          <div className="max-w-4xl mx-auto mb-20">
            <Card className="p-6 sm:p-10 bg-white border border-stone-200 shadow-xl rounded-3xl">
              <div className="text-center mb-8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  Ejemplo en Tiempo Real
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-stone-900 mt-3 tracking-tight">
                  Así procesa tu voz el motor de PGastos
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                {/* Lo que tú dices */}
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center shadow-sm">
                        <Mic className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Lo que tú dices:</p>
                        <p className="text-xs text-stone-500 font-medium">Dictado en 3 segundos</p>
                      </div>
                    </div>
                    <blockquote className="text-base font-semibold text-stone-800 italic bg-white p-4 rounded-xl border border-stone-200 shadow-xs leading-relaxed">
                      “Gasté 450 en despensa de fruta y leche y 85 en un café pagado con mi tarjeta Nu”
                    </blockquote>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-xs text-stone-500 font-mono">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span>Audio procesado en 180ms</span>
                  </div>
                </div>

                {/* Lo que registra la IA */}
                <div className="bg-stone-900 text-white rounded-2xl p-6 shadow-md flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4 pb-2 border-b border-stone-800">
                      <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
                        <Volume2 className="w-4 h-4 text-indigo-400" /> Desglose Automático
                      </span>
                      <span className="text-[11px] bg-stone-800 text-stone-300 px-2 py-0.5 rounded-full font-mono border border-stone-700">
                        Tarjeta Nu Detectada ✓
                      </span>
                    </div>

                    <div className="space-y-2.5 text-xs sm:text-sm">
                      <div className="flex items-center justify-between bg-stone-800/90 p-3 rounded-xl border border-stone-700/60">
                        <div>
                          <p className="font-bold text-white">Fruta y leche</p>
                          <p className="text-[11px] text-stone-400">Categoría: Despensa / Hogar</p>
                        </div>
                        <span className="font-black text-emerald-400 text-sm">$450.00 MXN</span>
                      </div>

                      <div className="flex items-center justify-between bg-stone-800/90 p-3 rounded-xl border border-stone-700/60">
                        <div>
                          <p className="font-bold text-white">Café</p>
                          <p className="text-[11px] text-stone-400">Categoría: Restaurantes y Cafés</p>
                        </div>
                        <span className="font-black text-emerald-400 text-sm">$85.00 MXN</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-stone-800 flex justify-between text-xs text-stone-400">
                    <span>Total registrado:</span>
                    <span className="font-bold text-white">$535.00 MXN</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Pillars & Benefits */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ListChecks className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Desglose de Artículos</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Si compraste varios artículos en una sola salida, la IA separa los conceptos en transacciones individuales con sus respectivas categorías.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <CreditCard className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Detección de Cuentas</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Menciona el banco o la tarjeta ("con BBVA", "con Nu", "en efectivo") y se vinculará directamente a tu saldo correcto sin pasos extra.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Zap className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Rapidez Total</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Registrar un gasto toma menos de 5 segundos. Ideal cuando vas caminando o saliendo de pagar sin tener que teclear números.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Layers className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Categorización Inteligente</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Asigna automáticamente tus categorías personalizadas o estándar sin que tengas que buscar en listas desplegables interminables.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Confirmación Inmediata</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Revisa los datos antes de guardarlos. Si necesitas ajustar un centavo o cambiar un detalle, tienes control total previo al registro.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Privacidad de Audio</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Tu voz se transcribe en tiempo real mediante canales encriptados y no se almacena ningún archivo de audio en servidores.
              </p>
            </Card>
          </div>

          {/* CTA Card */}
          <Card className="p-8 sm:p-14 bg-stone-900 text-white text-center rounded-3xl border border-stone-800 shadow-xl">
            <h2 className="text-2xl sm:text-4xl font-black mb-3 tracking-tight">
              ¿Listo para registrar gastos con tu voz?
            </h2>
            <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              Comienza hoy y descubre cómo mantener tus cuentas al día sin la fricción de teclear cada compra.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Cuenta Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-700 bg-stone-800 hover:bg-stone-700 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VozInfo;
